import { getContext } from '@/context';

export const PORTFOLIO_APP_TOUCH_POINT = 'com.picsart.apps.genai';

/** The host's touchpoint referrer header, read fresh (no cache) . */
export const getTouchpoint = (): string | null => {
  try {
    const headers = getContext().utils.api.getHeaders?.();
    return headers?.['x-touchpoint-referrer'] ?? null;
  } catch {
    return null;
  }
};

/**
 * Non-production surface override: `?touchpoint=portfolio` on the launch URL
 * forces the portfolio surface for E2E. The host reports a route-derived
 * touchpoint (e.g. `create-and-home` for /gen-ai/) that never matches the
 * portfolio one, so tests can't reach it otherwise. The host forwards query
 * params into the SDK location params. Ignored in production.
 */
const hasPortfolioOverride = (): boolean => {
  if (import.meta.env.VITE_NODE_ENV === 'production') return false;
  try {
    const value =
      getContext().general.location.getState?.()?.params?.touchpoint;
    return (Array.isArray(value) ? value[0] : value) === 'portfolio';
  } catch {
    return false;
  }
};

/**
 * Header gen.ai web adds to the host headers, and the value it sends.
 *
 * A contract with `gen-ai-web` (`GENAI_HOST_HEADER` in `src/miniapp/config.ts`).
 * gen.ai sends the portfolio app's own touchpoint, so `isPortfolioApp()` is true
 * there too; this is what tells the web site apart where the two must differ.
 */
const GENAI_HOST_HEADER = 'x-genai-host';
const GENAI_HOST_WEB = 'web';

/**
 * Whether the host is gen.ai web — which is also a portfolio surface.
 *
 * Arrives with the host's bootstrap payload, like the touchpoint: read before
 * `onOpen`, `getHeaders()` is `{}` and this is `false`.
 */
export const isGenaiWebHost = (): boolean => {
  try {
    return (
      getContext().utils.api.getHeaders?.()?.[GENAI_HOST_HEADER] ===
      GENAI_HOST_WEB
    );
  } catch {
    return false;
  }
};

/** Whether the miniapp is running inside the portfolio (gen-ai apps) surface. */
export const isPortfolioApp = (): boolean =>
  hasPortfolioOverride() || getTouchpoint() === PORTFOLIO_APP_TOUCH_POINT;

/** SDK location route contains the gen-ai segment (editor host's gen-ai section). */
export const isGenaiRoute = (): boolean => {
  try {
    const route = getContext().general.location.getState?.()?.route ?? '';
    // eslint-disable-next-line no-restricted-syntax -- canonical source of the surface check; callers use this predicate
    return route.includes('gen-ai');
  } catch {
    return false;
  }
};

/**
 * Whether this app is embedded in a host page that owns the URL.
 *
 * The other predicates here read the surface off the host's payload; this one
 * cannot — the fact is which module federation loaded, and only that module
 * knows. `src/entries/embed.tsx` marks it before the first render, so every
 * read below is already settled.
 *
 * What it changes: the host's path names the preset (see `HostRoutePreset`),
 * and the host's history is the user's history — this app must not push
 * entries onto it, intercept its back button, or treat its own screens as
 * places to come back to.
 */
let hostEmbed = false;

/** Called once by the `./Embed` entry, before `root.render`. */
export const markHostEmbed = (): void => {
  hostEmbed = true;
};

export const isHostEmbed = (): boolean => hostEmbed;
